import type { Webinar, WebinarPlatform, WebinarSpeaker } from '@proref/types';

// Display names for the meeting tools a webinar can run on. 'other' is intentionally vague here —
// `platformLabel` below sharpens it using the join link when there is one.
const PLATFORM_LABELS: Record<WebinarPlatform, string> = {
  teams: 'Microsoft Teams',
  zoom: 'Zoom',
  meet: 'Google Meet',
  other: 'Other',
};

// The options the create/edit form offers, in the order they're shown.
export const PLATFORM_OPTIONS: { value: WebinarPlatform; label: string }[] = (
  ['teams', 'zoom', 'meet', 'other'] as const
).map((value) => ({ value, label: PLATFORM_LABELS[value] }));

// Hostname → platform, for auto-selecting the dropdown when a host pastes a link. Matched as a
// SUFFIX on the hostname (never a substring of the whole URL) so a room id or a query parameter
// that happens to contain "zoom.us" can't decide the platform.
const HOST_PATTERNS: { suffix: string; platform: WebinarPlatform }[] = [
  { suffix: 'teams.microsoft.com', platform: 'teams' },
  { suffix: 'teams.live.com', platform: 'teams' },
  { suffix: 'zoom.us', platform: 'zoom' },
  { suffix: 'meet.google.com', platform: 'meet' },
];

function hostnameOf(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    // Half-typed URLs are normal while the host is still typing — not an error worth surfacing.
    return null;
  }
}

// Which platform a pasted link belongs to, or null when it isn't one we recognise (the form then
// leaves the host's own choice alone rather than forcing 'other' on them mid-typing).
export function detectPlatform(url: string): WebinarPlatform | null {
  const host = hostnameOf(url);
  if (!host) return null;
  const hit = HOST_PATTERNS.find((p) => host === p.suffix || host.endsWith(`.${p.suffix}`));
  return hit?.platform ?? null;
}

// What to call the platform on screen. For 'other' the bare word is useless to a referee deciding
// whether they can join, so the link's own hostname stands in ("Webex", "Jitsi") — that's why
// 'other' needs no second database column to name itself.
export function platformLabel(platform: WebinarPlatform | null, joinUrl: string | null): string | null {
  if (!platform) return null;
  if (platform !== 'other') return PLATFORM_LABELS[platform];
  const host = joinUrl ? hostnameOf(joinUrl) : null;
  if (!host) return PLATFORM_LABELS.other;
  // "webex.com" → "Webex": drop the TLD and capitalise the name that's left.
  const name = host.split('.').slice(0, -1).pop() ?? host;
  return name.charAt(0).toUpperCase() + name.slice(1);
}

// A webinar's speakers, tolerating a row that predates the field.
//
// The type says `speakers` is always an array, and for anything the API returns it is. But the
// query cache is PERSISTED to localStorage and rehydrated SYNCHRONOUSLY, bypassing the queryFn — so
// a returning client paints webinar rows saved before this field existed through today's components
// before any refetch can correct them, and `.length` on a missing key throws. `CACHE_BUSTER` in
// lib/query-client is the systematic fix (it discards those caches); this is the belt to its
// braces, and the reason that file's own comment says consumers must tolerate a missing field.
export function speakersOf(webinar: Pick<Webinar, 'speakers'>): WebinarSpeaker[] {
  return webinar.speakers ?? [];
}

// The people a webinar is advertised on, as one line: "Marta Brozmanová, Ivan Horvat".
export function speakerNames(webinar: Pick<Webinar, 'speakers'>): string | null {
  const names = speakersOf(webinar).map((s) => s.name);
  return names.length > 0 ? names.join(', ') : null;
}

// Who to credit in a compact meta line. Speakers win over the host: `host_name` is the ADMIN who
// created the webinar, which is rarely the person actually presenting it — the host is still shown
// in full in the detail modal, so nothing is lost by preferring the more useful name here.
export function creditLine(webinar: Pick<Webinar, 'speakers' | 'host_name'>): string | null {
  return speakerNames(webinar) ?? webinar.host_name;
}
